import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CircleAlert, Languages } from "lucide-react";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router";
import type { MenuDraftDto, TranslationDto } from "../../../shared/api-types";
import { TRANSLATION_LANG_NAMES, TRANSLATION_LANGS, type TranslationLang } from "../../../shared/constants";
import { normalizeForSearch } from "../../../shared/slug";
import { api } from "../../api/client";
import { keys, menuApi, useDraft, useMenus } from "../../api/queries";
import { StatusChip } from "../../components/kit";
import { useToast } from "../../components/Toast";
import { Alert, ICON } from "../../components/ui";
import { plural } from "../../lib/format";
import type { ShellContext } from "../AppShell";
import { mainMenu } from "./status";

// "Traduceri" (LOT 5; every translation language since 2026-11-02, DEC-107). Automatic translation of a menu into
// the chosen language (the model's answer is an unreviewed
// draft, never published by itself), correction by hand item by item, and the explicit approval of the
// language for the public menu (DEC-042/060). Untranslated or unapproved items fall back to Romanian.

interface Run {
  status: "none" | "queued" | "running" | "done" | "blocked" | "failed";
  translated: number;
  unsure: number;
  error: string | null;
  missing: number;
  approved: boolean;
}

const STATUS: Record<string, { tone: "success" | "info" | "warning" | "neutral"; label: string }> = {
  reviewed: { tone: "success", label: "Verificată" },
  manual: { tone: "success", label: "Scrisă de tine" },
  auto_unreviewed: { tone: "info", label: "Automată, neverificată" },
  needs_update: { tone: "warning", label: "De actualizat" },
  missing: { tone: "neutral", label: "Lipsă" },
};

type Item = {
  kind: "category" | "product";
  id: string;
  name: string;
  description: string;
  /** Where the item sits ("Categorie" or its category's name): tells apart products with the same name. */
  context: string;
  tr: TranslationDto | undefined;
};

type TrFilter = "all" | "missing" | "review" | "done";

/** Missing, to check (automatic, outdated or unsure) or verified (by hand or checked). */
export function trState(i: Pick<Item, "tr">): Exclude<TrFilter, "all"> {
  if (!i.tr) return "missing";
  if (i.tr.unsure || i.tr.status === "auto_unreviewed" || i.tr.status === "needs_update") return "review";
  return "done";
}

/** "engleză" → "Engleza" (the language as the subject of a sentence). */
const theLanguage = (name: string) => name.charAt(0).toUpperCase() + name.slice(1).replace(/ă$/, "a");

function Row({
  item,
  menuId,
  rid,
  lang,
  onSaved,
}: {
  item: Item;
  menuId: string;
  rid: string;
  lang: TranslationLang;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(item.tr?.name ?? "");
  const [description, setDescription] = useState(item.tr?.description ?? "");
  const [busy, setBusy] = useState(false);
  const s = STATUS[item.tr?.status ?? "missing"] ?? STATUS.missing;
  async function save(reviewed: boolean) {
    setBusy(true);
    try {
      await api("PUT", `/restaurants/${rid}/menus/${menuId}/translations/${lang}/${item.kind}/${item.id}`, {
        name,
        description,
        reviewed,
      });
      toast(reviewed ? "Traducere verificată." : "Traducere salvată.");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <li className="verify-row">
      <div className="stack" style={{ gap: 4, minWidth: 0 }}>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600 }}>{item.name}</span>
          <span className="caption muted">{item.context}</span>
          <StatusChip tone={s?.tone ?? "neutral"} icon={Languages} label={s?.label ?? ""} />
          {item.tr?.unsure && <StatusChip tone="warning" icon={CircleAlert} label="Traducere nesigură" />}
        </div>
        <span className="caption muted">{item.tr ? item.tr.name : "Fără traducere: apare în română."}</span>
        {open && (
          <div className="stack" style={{ gap: 8, maxWidth: 520 }}>
            <label className="stack" style={{ gap: 4 }}>
              <span className="field-label">Nume în {TRANSLATION_LANG_NAMES[lang]}</span>
              <input
                className="field-input"
                value={name}
                maxLength={150}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            {item.kind === "product" && (
              <label className="stack" style={{ gap: 4 }}>
                <span className="field-label">Descriere în {TRANSLATION_LANG_NAMES[lang]}</span>
                <textarea
                  className="field-input"
                  rows={2}
                  value={description}
                  maxLength={1000}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
            )}
            {item.description && <span className="caption muted">În română: {item.description}</span>}
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy || !name.trim()}
                onClick={() => save(true)}
              >
                <Check {...ICON} aria-hidden="true" /> Salvează ca verificată
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={busy || !name.trim()}
                onClick={() => save(false)}
              >
                Salvează
              </button>
            </div>
          </div>
        )}
      </div>
      {!open && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>
          {item.tr ? "Corectează" : "Scrie traducerea"}
        </button>
      )}
    </li>
  );
}

export function TranslationsPage() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const qc = useQueryClient();
  const toast = useToast();
  const menus = useMenus(rid);
  const active = (menus.data ?? []).filter((m) => !m.archived);
  const [menuId, setMenuId] = useState<string | null>(null);
  const mid = menuId ?? mainMenu(menus.data ?? [])?.id ?? null;
  const [query, setQuery] = useState("");
  const [trFilter, setTrFilter] = useState<TrFilter>("all");
  const [lang, setLang] = useState<TranslationLang>("en");
  const langName = TRANSLATION_LANG_NAMES[lang];
  const draft = useDraft(rid, mid);
  const run = useQuery({
    queryKey: ["translation-run", rid, mid, lang],
    enabled: !!mid,
    queryFn: () => api<Run>("GET", `/restaurants/${rid}/menus/${mid}/translations/${lang}/run`),
    refetchInterval: (q) =>
      q.state.data && ["queued", "running"].includes(q.state.data.status) ? 2500 : false,
  });
  const [busy, setBusy] = useState(false);
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["translation-run", rid, mid] }),
      ...(mid ? [qc.invalidateQueries({ queryKey: keys.draft(rid, mid) })] : []),
      qc.invalidateQueries({ queryKey: keys.menus(rid) }),
    ]);
  // A finished run brings its translations into the list.
  const status = run.data?.status;
  useEffect(() => {
    if (status === "done" && mid) void qc.invalidateQueries({ queryKey: keys.draft(rid, mid) });
  }, [status, mid, rid, qc]);

  const d: MenuDraftDto | undefined = draft.data;
  const catName = new Map((d?.categories ?? []).map((c) => [c.id, c.name]));
  const items: Item[] = d
    ? [
        ...d.categories.map((c) => ({
          kind: "category" as const,
          id: c.id,
          name: c.name,
          description: "",
          context: "Categorie",
          tr: c.translations[lang],
        })),
        ...d.products.map((p) => ({
          kind: "product" as const,
          id: p.id,
          name: p.name,
          description: p.description,
          context: catName.get(p.categoryId) ?? "",
          tr: p.translations[lang],
        })),
      ].sort((a, b) => Number(!!b.tr?.unsure) - Number(!!a.tr?.unsure))
    : [];
  const missing = items.filter((i) => !i.tr).length;
  const toReview = items.filter((i) => trState(i) === "review").length;
  const verified = items.filter((i) => trState(i) === "done").length;
  const approved = run.data?.approved ?? false;
  const nq = normalizeForSearch(query);
  const shown = items.filter(
    (i) =>
      (trFilter === "all" || trState(i) === trFilter) &&
      (!nq ||
        normalizeForSearch(i.name).includes(nq) ||
        normalizeForSearch(i.tr?.name ?? "").includes(nq) ||
        normalizeForSearch(i.context).includes(nq)),
  );
  const The = theLanguage(langName);

  async function act(fn: () => Promise<unknown>, done: string) {
    setBusy(true);
    try {
      await fn();
      toast(done);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      await refresh();
      setBusy(false);
    }
  }

  return (
    <>
      <div className="stack" style={{ gap: 6 }}>
        <h1 className="h1">Traduceri</h1>
        <p className="page-sub" style={{ margin: 0, maxWidth: 680 }}>
          Meniul în alte limbi, pentru turiști. Traducerile automate sunt ciorne: apar în meniul public doar
          după ce aprobi limba. Prețurile și gramajele rămân cele din meniul în română.
        </p>
      </div>
      {active.length > 1 && (
        <label className="stack" style={{ gap: 4, maxWidth: 320 }}>
          <span className="field-label">Meniul</span>
          <select className="field-input" value={mid ?? ""} onChange={(e) => setMenuId(e.target.value)}>
            {active.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="stack" style={{ gap: 4, maxWidth: 320 }}>
        <span className="field-label">Limba</span>
        <select
          className="field-input"
          value={lang}
          onChange={(e) => setLang(e.target.value as TranslationLang)}
        >
          {TRANSLATION_LANGS.map((l) => (
            <option key={l} value={l}>
              {TRANSLATION_LANG_NAMES[l].charAt(0).toUpperCase() + TRANSLATION_LANG_NAMES[l].slice(1)}
            </option>
          ))}
        </select>
      </label>
      {!mid && menus.isSuccess && <Alert tone="info">Creează mai întâi un meniu.</Alert>}
      {d && mid && (
        <>
          <section className="card stack" style={{ gap: 12 }} aria-label={`Traducerea în ${langName}`}>
            <p style={{ margin: 0, fontWeight: 600 }}>
              {approved
                ? `${The} este vizibilă clienților (după publicare).`
                : `${The} nu este încă vizibilă clienților.`}
            </p>
            <p className="caption muted" style={{ margin: 0 }}>
              {items.length - missing} din {items.length} elemente traduse — produse și categorii · {toReview}{" "}
              de verificat · {verified} verificate.{" "}
              {missing
                ? `${plural(missing, "element apare", "elemente apar")} clienților în română până le traduci.`
                : ""}
            </p>
            <div
              className="progress"
              role="progressbar"
              aria-label={`Elemente traduse în ${langName}`}
              aria-valuenow={items.length - missing}
              aria-valuemin={0}
              aria-valuemax={items.length}
            >
              <div
                style={{
                  width: `${items.length ? Math.round(((items.length - missing) / items.length) * 100) : 0}%`,
                }}
              />
            </div>
            {run.data && ["queued", "running"].includes(run.data.status) && (
              <Alert tone="info" live>
                Traducem meniul… ({plural(run.data.missing, "element rămas", "elemente rămase")})
              </Alert>
            )}
            {run.data?.status === "blocked" && <Alert tone="warning">{run.data.error}</Alert>}
            {run.data?.status === "failed" && <Alert tone="error">{run.data.error}</Alert>}
            {run.data?.status === "done" && run.data.unsure > 0 && (
              <Alert tone="warning">
                {plural(
                  run.data.unsure,
                  "traducere este marcată ca nesigură",
                  "traduceri sunt marcate ca nesigure",
                )}
                : le găsești primele în listă.
              </Alert>
            )}
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy || !missing || ["queued", "running"].includes(run.data?.status ?? "")}
                onClick={() =>
                  act(
                    () => api("POST", `/restaurants/${rid}/menus/${mid}/translations/${lang}/auto`),
                    "Am pornit traducerea automată.",
                  )
                }
              >
                <Languages {...ICON} aria-hidden="true" />
                {missing ? `Traduce automat (${missing})` : "Totul este tradus"}
              </button>
              {!approved && items.length - missing > 0 && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() =>
                    act(
                      () => menuApi(rid).confirmTranslations(mid, lang, d.menu.draftRevision),
                      `Limba ${langName} a fost aprobată. Apare în meniul public după publicare.`,
                    )
                  }
                >
                  <Check {...ICON} aria-hidden="true" />
                  Am verificat — aprobă limba {langName}
                </button>
              )}
              {approved && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={busy}
                  onClick={() =>
                    act(
                      () =>
                        api("PUT", `/restaurants/${rid}/menus/${mid}/languages/${lang}`, {
                          status: "hidden",
                        }),
                      `Limba ${langName} nu va mai apărea în meniul public după publicare.`,
                    )
                  }
                >
                  Ascunde limba {langName}
                </button>
              )}
            </div>
          </section>
          <div className="list-tools-more">
            <div className="list-tools-row">
              <search className="search-field" style={{ flex: "1 1 220px" }}>
                <input
                  type="search"
                  aria-label="Caută în traduceri"
                  placeholder="Caută produse sau categorii…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </search>
              <label className="sr-only" htmlFor="tr-filter">
                Arată
              </label>
              <select
                id="tr-filter"
                className="field-input filter-pick"
                value={trFilter}
                onChange={(e) => setTrFilter(e.target.value as TrFilter)}
              >
                <option value="all">Toate ({items.length})</option>
                <option value="missing">Lipsă ({missing})</option>
                <option value="review">De verificat ({toReview})</option>
                <option value="done">Verificate ({verified})</option>
              </select>
            </div>
          </div>
          <section className="card stack" style={{ gap: 12 }} aria-label="Elemente">
            <ul className="verify-list">
              {shown.length === 0 && (
                <li className="verify-row">
                  <span className="muted">Nimic nu se potrivește.</span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setQuery("");
                      setTrFilter("all");
                    }}
                  >
                    Arată tot
                  </button>
                </li>
              )}
              {shown.map((i) => (
                <Row
                  lang={lang}
                  key={`${i.kind}-${i.id}-${i.tr?.name ?? ""}`}
                  item={i}
                  menuId={mid}
                  rid={rid}
                  onSaved={() => void refresh()}
                />
              ))}
            </ul>
          </section>
        </>
      )}
    </>
  );
}
